import { useState } from 'react';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useApi } from '../hooks/useApi';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { HelpTip } from '../components/HelpTip';
import { Confirm } from '../components/Confirm';
import { FieldHint } from '../components/FieldHint';
import { api, extractErrorMessage, getAccessToken } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';

interface Cuota {
  id?: number;
  orden?: number;
  dias: number;
  porcentaje: number;
  interes: number;
  descripcion: string;
}

interface CondicionPago {
  id: number;
  nombre: string;
  tipo: 'contado' | 'cuotas' | 'anticipado' | 'mixto';
  dias: number;
  referencia: string;
  activo: boolean;
  descripcion?: string;
  cuotas?: Cuota[];
  _count?: { cuotas: number };
  descuentoProntoPagoPct?: string | number | null;
  diasProntoPago?: number | null;
}

const tipoLabels: Record<string, string> = {
  contado: 'Contado',
  cuotas: 'Cuotas',
  anticipado: 'Anticipo',
  mixto: 'Mixto',
};

const tipoColors: Record<string, string> = {
  contado: 'bg-green-100 text-green-700',
  cuotas: 'bg-blue-100 text-blue-700',
  anticipado: 'bg-amber-100 text-amber-700',
  mixto: 'bg-purple-100 text-purple-700',
};

const refLabels: Record<string, string> = {
  oc: 'OC',
  remito: 'Remito',
  factura: 'Factura',
};

const emptyCuota = (): Cuota => ({ dias: 0, porcentaje: 0, interes: 0, descripcion: '' });

const emptyForm = () => ({
  nombre: '',
  tipo: 'contado' as CondicionPago['tipo'],
  dias: 0,
  referencia: 'factura' as CondicionPago['referencia'],
  descripcion: '',
  activo: true,
  cuotas: [] as Cuota[],
  // Pronto pago: vacíos = sin descuento (van los dos o ninguno)
  descuentoProntoPagoPct: '',
  diasProntoPago: '',
});

export function CondicionPagoPage() {
  const { data: response, loading, refetch } = useApi<{ data: CondicionPago[]; total: number }>('/api/v3/condiciones-pago');
  const data = response?.data ?? [];
  const [filters, setFilters] = useState<Record<string, string>>({});

  const filtered = data.filter(row =>
    Object.entries(filters).every(([key, val]) => {
      if (!val) return true;
      if (key === 'activo') {
        const target = val === 'true' || val === 'si' || val === 'sí';
        return row.activo === target;
      }
      if (key === 'tipo') {
        return row.tipo.toLowerCase().includes(val.toLowerCase());
      }
      return String((row as any)[key] ?? '').toLowerCase().includes(val.toLowerCase());
    }),
  );

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CondicionPago | null>(null);
  const [formError, setFormError] = useState('');
  const cerrarForm = () => { setOpen(false); setEditId(null); setForm(emptyForm()); setFormError(''); };
  const cierreForm = useCierreConfirmado(useHayCambios(form, open), cerrarForm);
  const toast = useToast();
  const { can } = useAuth();

  const showCuotas = form.tipo !== 'contado';

  const handleTipoChange = (tipo: string) => {
    const newTipo = tipo as CondicionPago['tipo'];
    if (newTipo === 'contado') {
      setForm({ ...form, tipo: newTipo, cuotas: [] });
    } else if (form.cuotas.length === 0) {
      const cuotas = newTipo === 'anticipado'
        ? [{ ...emptyCuota(), dias: 0 }, emptyCuota()]
        : [emptyCuota()];
      setForm({ ...form, tipo: newTipo, cuotas });
    } else {
      setForm({ ...form, tipo: newTipo });
    }
  };

  const addCuota = () => {
    setForm({ ...form, cuotas: [...form.cuotas, emptyCuota()] });
  };

  const removeCuota = (idx: number) => {
    const cuotas = form.cuotas.filter((_, i) => i !== idx);
    // If anticipado and last row removed, don't allow going below 1
    if (form.tipo === 'anticipado' && cuotas.length < 1) return;
    setForm({ ...form, cuotas });
  };

  const updateCuota = (idx: number, field: keyof Cuota, value: string | number) => {
    const cuotas = [...form.cuotas];
    cuotas[idx] = { ...cuotas[idx], [field]: value };
    setForm({ ...form, cuotas });
  };

  const cuotasSum = form.cuotas.reduce((s, c) => s + Number(c.porcentaje || 0), 0);
  const cuotasValid = Math.abs(cuotasSum - 100) < 0.01;

  // Checks that don't already have a visual indicator elsewhere (the % sum has the
  // "Suma: X% ✓/—debe sumar 100%" cosmetic hint below the cuotas grid).
  const validateOtherRules = (): string | null => {
    if (!form.nombre.trim()) return 'El nombre es obligatorio';
    if (form.tipo !== 'contado' && form.cuotas.length === 0) return 'Debe agregar al menos una cuota';
    if ((form.descuentoProntoPagoPct === '') !== (form.diasProntoPago === '')) return 'El pronto pago requiere el % y los días (o ninguno de los dos)';
    return null;
  };

  const validateAnticipado = (): string | null => {
    if (form.tipo === 'anticipado') {
      if (form.cuotas.length < 2) return 'El tipo "Anticipo" requiere al menos 2 cuotas';
      if (Number(form.cuotas[0]?.dias || 0) !== 0) return 'La primera cuota de Anticipo debe tener días=0';
    }
    return null;
  };

  const validateForm = (): string | null => {
    const other = validateOtherRules();
    if (other) return other;
    if (form.tipo !== 'contado' && form.cuotas.length > 0 && !cuotasValid) {
      return `La suma de porcentajes debe ser 100% (actual: ${cuotasSum.toFixed(2)}%)`;
    }
    return validateAnticipado();
  };

  const applyPreset = (preset: string) => {
    if (preset === 'saldo') {
      setForm({
        ...form,
        cuotas: [
          { ...emptyCuota(), dias: 0, porcentaje: 50 },
          { ...emptyCuota(), dias: 30, porcentaje: 50 },
        ],
      });
    } else if (preset === 'n-cuotas') {
      setForm({
        ...form,
        cuotas: [
          { ...emptyCuota(), dias: 0, porcentaje: 30 },
          { ...emptyCuota(), dias: 30, porcentaje: 23.33 },
          { ...emptyCuota(), dias: 60, porcentaje: 23.33 },
          { ...emptyCuota(), dias: 90, porcentaje: 23.34 },
        ],
      });
    } else {
      // personalizado — manual mode, reset to default 2 cuotas
      setForm({
        ...form,
        cuotas: [
          { ...emptyCuota(), dias: 0, porcentaje: 50 },
          { ...emptyCuota(), dias: 30, porcentaje: 50 },
        ],
      });
    }
  };

  const handleCreate = async () => {
    const err = validateForm();
    if (err) {
      setFormError(validateOtherRules() || validateAnticipado() || '');
      toast.addToast(err, 'error');
      return;
    }
    setFormError('');
    setSubmitting(true);
    try {
      const body: any = {
        nombre: form.nombre.trim(),
        tipo: form.tipo,
        referencia: form.referencia,
        activo: form.activo,
        descripcion: form.descripcion || undefined,
        descuentoProntoPagoPct: form.descuentoProntoPagoPct === '' ? null : Number(form.descuentoProntoPagoPct),
        diasProntoPago: form.diasProntoPago === '' ? null : Number(form.diasProntoPago),
      };
      if (form.tipo === 'contado') {
        body.dias = form.dias;
      }
      if (form.tipo !== 'contado') {
        body.cuotas = form.cuotas.map(c => ({
          dias: c.dias,
          porcentaje: c.porcentaje,
          interes: c.interes || 0,
          descripcion: c.descripcion || undefined,
        }));
      }
      if (editId) {
        await api.patch(`/api/v3/condiciones-pago/${editId}`, body);
        toast.addToast('Condición actualizada', 'success');
      } else {
        await api.post('/api/v3/condiciones-pago', body);
        toast.addToast('Condición creada', 'success');
      }
      cerrarForm();
      refetch();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async (item: CondicionPago) => {
    if (!can('condiciones-pago', 'editar')) { toast.addToast('No tenés permiso para realizar esta acción', 'error'); return; }
    try {
      const full = await api.get<CondicionPago>(`/api/v3/condiciones-pago/${item.id}`);
      setForm({
        nombre: full.nombre,
        tipo: full.tipo,
        dias: full.dias || 0,
        referencia: (full.referencia as CondicionPago['referencia']) || 'factura',
        activo: full.activo,
        descripcion: full.descripcion || '',
        cuotas: (full.cuotas || []).map(c => ({
          id: c.id,
          orden: c.orden,
          dias: c.dias,
          porcentaje: Number(c.porcentaje),
          interes: Number(c.interes),
          descripcion: c.descripcion || '',
        })),
        descuentoProntoPagoPct: full.descuentoProntoPagoPct != null ? String(Number(full.descuentoProntoPagoPct)) : '',
        diasProntoPago: full.diasProntoPago != null ? String(full.diasProntoPago) : '',
      });
      setEditId(full.id);
      setFormError('');
      setOpen(true);
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (!can('condiciones-pago', 'eliminar')) { toast.addToast('No tenés permiso para realizar esta acción', 'error'); return; }
    setSubmitting(true);
    try {
      await api.delete(`/api/v3/condiciones-pago/${deleteTarget.id}`);
      toast.addToast(`"${deleteTarget.nombre}" eliminada`, 'success');
      setConfirmDelete(false);
      setDeleteTarget(null);
      refetch();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
    finally { setSubmitting(false); }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await api.upload<{ creados?: number; fallos?: number; errores?: string[] }>(
        '/api/v3/condiciones-pago/importar',
        file,
      );
      const msgs: string[] = [];
      if (r.creados) msgs.push(`${r.creados} creadas`);
      if (r.fallos) msgs.push(`${r.fallos} fallos`);
      toast.addToast(`Importación: ${msgs.join(', ')}`, r.fallos ? 'info' : 'success');
      refetch();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
    e.target.value = '';
  };

  const downloadTemplate = async (format: 'csv' | 'xlsx' = 'csv') => {
    try {
      const token = getAccessToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const url = format === 'xlsx'
        ? '/api/v3/condiciones-pago/plantilla?format=xlsx'
        : '/api/v3/condiciones-pago/plantilla';
      const res = await fetch(url, {
        headers,
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || err?.message || 'Error al descargar plantilla');
      }
      const blob = await res.blob();
      const urlObj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = format === 'xlsx'
        ? 'plantilla_condiciones_pago.xlsx'
        : 'plantilla_condiciones_pago.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(urlObj);
      toast.addToast('Plantilla descargada', 'success');
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    }
  };

  const columns = [
    {
      key: 'nombre',
      header: 'Nombre',
      help: 'Nombre de la condición de pago',
    },
    {
      key: 'tipo',
      header: 'Tipo',
      render: (r: CondicionPago) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tipoColors[r.tipo] || 'bg-slate-100 text-slate-600'}`}>
          {tipoLabels[r.tipo] || r.tipo}
        </span>
      ),
      help: 'Contado: pago único. Cuotas: partes iguales. Anticipo: anticipo + saldo. Mixto: libre.',
    },
    {
      key: 'dias',
      header: 'Plazo',
      render: (r: CondicionPago) =>
        r.tipo === 'contado'
          ? `${r.dias || 0} días`
          : `${r._count?.cuotas ?? r.cuotas?.length ?? 0} cuotas`,
      filterable: false,
      help: 'Días para contado, cantidad de cuotas para otros tipos',
    },
    {
      key: 'referencia',
      header: 'Referencia',
      render: (r: CondicionPago) => (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
          {refLabels[r.referencia] || r.referencia || '—'}
        </span>
      ),
      help: 'Documento de referencia para calcular vencimiento: OC, Remito o Factura',
    },
    {
      key: 'prontoPago',
      header: 'Pronto pago',
      render: (r: CondicionPago) => r.descuentoProntoPagoPct != null && r.diasProntoPago != null
        ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700">{Number(r.descuentoProntoPagoPct)}% a {r.diasProntoPago} días</span>
        : <span className="text-slate-400">—</span>,
      filterable: false,
      help: 'Descuento si se paga dentro de esos días desde la fecha de referencia',
    },
    {
      key: 'activo',
      header: 'Activo',
      render: (r: CondicionPago) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.activo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {r.activo ? 'Sí' : 'No'}
        </span>
      ),
      filterable: false,
      help: 'Si está activo aparece en los selectores de Compras y Facturas',
    },
    {
      key: 'actions',
      header: '',
      render: (r: CondicionPago) => (
        <button
          onClick={e => {
            e.stopPropagation();
            setDeleteTarget(r);
            setConfirmDelete(true);
          }}
          disabled={!can('condiciones-pago', 'eliminar')}
          className="text-red-500 hover:text-red-700 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          title={!can('condiciones-pago', 'eliminar') ? 'No tenés permiso para realizar esta acción' : 'Eliminar condición de pago'}
        >
          Eliminar
        </button>
      ),
      filterable: false,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Condiciones de Pago</h1>
          <HelpTip
            tips={[
              'Definí condiciones de pago para usar en órdenes de compra y facturas',
              'Contado: pago único a N días. Cuotas: pagos en partes iguales.',
              'Anticipo: anticipo + saldo. Mixto: cualquier estructura de cuotas.',
              'La suma de porcentajes de las cuotas debe ser 100%.',
              'El tipo define cómo se calcula la fecha de vencimiento automática en facturas.',
            ]}
          />
          <p className="text-slate-500 text-sm mt-1">Plazos y condiciones de pago</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => downloadTemplate('csv')}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium"
            title="Descargar plantilla CSV para importar"
          >
            📥 CSV
          </button>
          <button
            onClick={() => downloadTemplate('xlsx')}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium"
            title="Descargar plantilla XLSX para importar"
          >
            📥 XLSX
          </button>
          <label
            onClick={(e) => { if (!can('condiciones-pago', 'crear')) e.preventDefault(); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${!can('condiciones-pago', 'crear') ? 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-50' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer'}`}
            title={!can('condiciones-pago', 'crear') ? 'No tenés permiso para realizar esta acción' : 'Importar condiciones de pago desde archivo CSV o XLSX'}
          >
            📄 Importar
            <input type="file" accept=".csv,.xlsx" onChange={handleImport} className="hidden" disabled={!can('condiciones-pago', 'crear')} />
          </label>
          <Button
            onClick={() => {
              setEditId(null);
              setForm(emptyForm());
              setFormError('');
              setOpen(true);
            }}
            disabled={!can('condiciones-pago', 'crear')}
            title={!can('condiciones-pago', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}
          >
            + Nueva
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        onRowClick={handleEdit}
        filters={filters}
        onFilterChange={(k, v) =>
          setFilters(f =>
            v
              ? { ...f, [k]: v }
              : Object.fromEntries(Object.entries(f).filter(([key]) => key !== k)),
          )
        }
      />

      {/* Quick filter chips for activo/tipo */}
      <div className="flex items-center gap-2 -mt-2">
        <span className="text-xs text-slate-400">Filtros rápidos:</span>
        {['', 'true', 'false'].map(val => (
          <button
            key={`activo-${val}`}
            onClick={() =>
              setFilters(f => (val ? { ...f, activo: val } : Object.fromEntries(Object.entries(f).filter(([k]) => k !== 'activo'))))
            }
            className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
              filters.activo === val && val !== ''
                ? 'bg-brand-100 text-brand-700'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {val === '' ? 'Todos' : val === 'true' ? '✓ Activos' : '✕ Inactivos'}
          </button>
        ))}
        <span className="text-slate-300 mx-1">|</span>
        {['', 'contado', 'cuotas', 'anticipado', 'mixto'].map(tipo => (
          <button
            key={`tipo-${tipo}`}
            onClick={() =>
              setFilters(f => (tipo ? { ...f, tipo } : Object.fromEntries(Object.entries(f).filter(([k]) => k !== 'tipo'))))
            }
            className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
              filters.tipo === tipo && tipo !== ''
                ? 'bg-brand-100 text-brand-700'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {tipo === '' ? 'Todos' : tipoLabels[tipo] || tipo}
          </button>
        ))}
      </div>

      <Modal
        open={open}
        cierre={cierreForm}
        title={editId ? `Editar Condición #${editId}` : 'Nueva Condición de Pago'}
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Nombre{' '}
                <FieldHint text="Nombre descriptivo. Ej: 'Contado', '30 días', '3 cuotas iguales'" />
              </label>
              <input
                value={form.nombre}
                onChange={e => setForm({ ...form, nombre: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ej: 30 días, Contado..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Tipo{' '}
                <FieldHint text="Define si es pago único o en cuotas. Cambiar el tipo modifica las opciones de cuotas." />
              </label>
              <select
                value={form.tipo}
                onChange={e => handleTipoChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="contado">Contado</option>
                <option value="cuotas">Cuotas</option>
                <option value="anticipado">Anticipo</option>
                <option value="mixto">Mixto</option>
              </select>
            </div>
            {form.tipo === 'contado' && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Días{' '}
                  <FieldHint text="Plazo de pago en días desde la fecha de referencia" />
                </label>
                <input
                  type="number"
                  value={form.dias}
                  onChange={e => setForm({ ...form, dias: +e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  min={0}
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">
                Referencia{' '}
                <FieldHint text="Documento desde el cual se calcula la fecha de vencimiento" />
              </label>
              <select
                value={form.referencia}
                onChange={e => setForm({ ...form, referencia: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="oc">Orden de Compra</option>
                <option value="remito">Remito</option>
                <option value="factura">Factura</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Pronto pago: descuento %{' '}
                <FieldHint text="Descuento si se paga dentro de los días de pronto pago desde la fecha de referencia. Vacío = sin descuento" />
              </label>
              <input
                type="number"
                value={form.descuentoProntoPagoPct}
                onChange={e => setForm({ ...form, descuentoProntoPagoPct: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                min={0.01} max={100} step="0.01" placeholder="Ej: 2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Pronto pago: días{' '}
                <FieldHint text="Plazo para pagar con descuento, en días desde la fecha de referencia. Es independiente del vencimiento" />
              </label>
              <input
                type="number"
                value={form.diasProntoPago}
                onChange={e => setForm({ ...form, diasProntoPago: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                min={0} placeholder="Ej: 10"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Descripción</label>
              <input
                value={form.descripcion}
                onChange={e => setForm({ ...form, descripcion: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Opcional — nota interna"
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={e => setForm({ ...form, activo: e.target.checked })}
                  className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm font-medium">Activo</span>
              </label>
            </div>
          </div>

          {formError && <p className="text-red-600 text-sm">{formError}</p>}

          {/* Cuotas sub-form (visible for non-contado types) */}
          {showCuotas && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700">
                  Cuotas ({form.cuotas.length})
                  {form.tipo === 'anticipado' && (
                    <span className="text-amber-600 text-xs ml-2 font-normal">
                      — primera cuota días=0, al menos 2 cuotas
                    </span>
                  )}
                  {form.tipo === 'cuotas' && (
                    <span className="text-blue-600 text-xs ml-2 font-normal">
                      — se recomiendan porcentajes iguales
                    </span>
                  )}
                </h3>
                <button
                    onClick={addCuota}
                    className="text-brand-600 text-sm font-medium hover:text-brand-700"
                  >
                    + Agregar cuota
                  </button>
              </div>

              {/* Preset buttons for anticipado */}
              {form.tipo === 'anticipado' && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-slate-500">Plantillas:</span>
                  <button
                    type="button"
                    onClick={() => applyPreset('n-cuotas')}
                    className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-600 hover:bg-amber-100 hover:text-amber-700 transition-colors"
                  >
                    Anticipo + 3 Cuotas
                  </button>
                  <button
                    type="button"
                    onClick={() => applyPreset('saldo')}
                    className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-600 hover:bg-amber-100 hover:text-amber-700 transition-colors"
                  >
                    Anticipo + Saldo
                  </button>
                  <button
                    type="button"
                    onClick={() => applyPreset('personalizado')}
                    className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-600 hover:bg-amber-100 hover:text-amber-700 transition-colors"
                  >
                    Personalizado
                  </button>
                </div>
              )}

              <div className="overflow-x-auto">
                <div className="grid grid-cols-12 gap-1 text-[10px] text-slate-400 px-1 mb-1 min-w-[500px]">
                  <span className="text-center">#</span>
                  <span className="col-span-2">Días</span>
                  <span className="col-span-2">% Pago</span>
                  <span className="col-span-2">Interés %</span>
                  <span className="col-span-3">Descripción</span>
                  <span></span>
                </div>
                <div className="space-y-2">
                  {form.cuotas.map((cuota, i) => (
                    <div key={i} className="grid grid-cols-12 gap-1 text-xs min-w-[500px]">
                      <div className="px-2 py-1.5 bg-slate-50 rounded text-center font-medium text-slate-600">
                        {i + 1}
                      </div>
                      <input
                        type="number"
                        value={cuota.dias}
                        onChange={e => updateCuota(i, 'dias', +e.target.value)}
                        className="col-span-2 px-2 py-1.5 border rounded text-xs"
                        min={0}
                        disabled={form.tipo === 'anticipado' && i === 0}
                        title={
                          form.tipo === 'anticipado' && i === 0
                            ? 'Anticipo: siempre días=0'
                            : 'Días desde la fecha de referencia'
                        }
                      />
                      <input
                        type="number"
                        value={cuota.porcentaje}
                        onChange={e => updateCuota(i, 'porcentaje', parseFloat(e.target.value) || 0)}
                        className="col-span-2 px-2 py-1.5 border rounded text-xs"
                        min={0}
                        max={100}
                        step="0.01"
                      />
                      <input
                        type="number"
                        value={cuota.interes}
                        onChange={e => updateCuota(i, 'interes', parseFloat(e.target.value) || 0)}
                        className="col-span-2 px-2 py-1.5 border rounded text-xs"
                        min={0}
                        step="0.01"
                      />
                      <input
                        value={cuota.descripcion}
                        onChange={e => updateCuota(i, 'descripcion', e.target.value)}
                        className="col-span-3 px-2 py-1.5 border rounded text-xs"
                        placeholder="Opcional"
                      />
                      <button
                        onClick={() => removeCuota(i)}
                        className="text-red-500 text-xs self-center hover:text-red-700"
                        title="Eliminar cuota"
                        aria-label="Eliminar cuota"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                {/* Percentage sum indicator */}
                {form.cuotas.length > 0 && (
                  <div
                    className={`mt-2 text-xs font-medium ${cuotasValid ? 'text-green-600' : 'text-red-500'}`}
                  >
                    Suma: {cuotasSum.toFixed(2)}%
                    {cuotasValid ? ' ✓' : ` — debe sumar 100% (diferencia: ${(100 - cuotasSum).toFixed(2)}%)`}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={cierreForm.cerrar}
              className="px-4 py-2 text-sm"
            >
              Cancelar
            </button>
            <Button
              onClick={handleCreate}
              loading={submitting}
              disabled={editId ? !can('condiciones-pago', 'editar') : !can('condiciones-pago', 'crear')}
              title={(editId ? !can('condiciones-pago', 'editar') : !can('condiciones-pago', 'crear')) ? 'No tenés permiso para realizar esta acción' : undefined}
            >
              {submitting ? 'Guardando...' : editId ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </div>
      </Modal>

      <Confirm
        open={confirmDelete}
        title="Eliminar Condición de Pago"
        message={`¿Estás seguro de eliminar "${deleteTarget?.nombre}"?${
          deleteTarget?.tipo !== 'contado' && (deleteTarget?._count?.cuotas ?? 0) > 0
            ? ` Se eliminarán también sus ${deleteTarget?._count?.cuotas ?? deleteTarget?.cuotas?.length ?? 0} cuotas.`
            : ''
        }`}
        confirmLabel="Eliminar"
        danger
        onConfirm={handleDelete}
        onCancel={() => {
          setConfirmDelete(false);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}
